import { Component, OnInit, OnDestroy } from '@angular/core';
import { SeoService } from '../../shared/seo.service';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface Article {
  title: string;
  excerpt: string;
  content: string;
  fullContent: string;
  tags: string[];
  expanded: boolean;
  // populated from parent
  categoryLabel?: string;
  categoryIcon?: string;
}

interface HelpCategory {
  id: string;
  title: string;
  subtitle: string;
  icon: string;
  tag: string;
  tagColor: string;
  articles: Article[];
}

interface SearchResult {
  title: string;
  excerpt: string;
  fullContent: string;
  categoryLabel: string;
  categoryIcon: string;
  tags: string[];
}

@Component({
  selector: 'app-help-center',
  templateUrl: './help-center.component.html',
  styleUrls: ['./help-center.component.scss']
})
export class HelpCenterComponent implements OnInit {

  constructor(private seoService: SeoService) {}

  searchTerm = '';
  searchResults: SearchResult[] = [];
  openedArticle: SearchResult | null = null;
  helpfulSubmitted = false;
  helpfulResponse = '';

  readonly popularSearches = [
    'How to log in',
    'Reset password',
    'Barter coaching',
    'ICF hours',
    'Live session schedule',
    'Upgrade program'
  ];

  readonly helpCategories: HelpCategory[] = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      subtitle: 'First steps after enrolling — logging in, navigating the platform, and setting up your profile.',
      icon: 'fa-solid fa-rocket',
      tag: 'Basics',
      tagColor: 'navy',
      articles: [
        {
          title: 'How do I log in to the NEXGEN platform?',
          excerpt: 'Instructions for accessing the academy platform for the first time.',
          content: `<p>Go to <strong>academy.nexgencoaches.com/login</strong> and enter your email and password. Your account is created automatically when you enroll.</p>
                    <p>If you haven't received your login credentials by email within 15 minutes of enrolling, check your spam folder, then contact us.</p>`,
          fullContent: `<p>Your NEXGEN account is created automatically when you complete enrollment. Here's how to access it:</p>
                        <ol>
                          <li>Go to <strong>academy.nexgencoaches.com/login</strong></li>
                          <li>Enter the email address you used during enrollment</li>
                          <li>Enter your password (set during enrollment or sent by email)</li>
                          <li>Click "Sign In"</li>
                        </ol>
                        <h4>Haven't received your credentials?</h4>
                        <p>Check your spam or junk folder for an email from NEXGEN. If it's not there after 15 minutes, contact us via the Contact page or call +1 (248) 270-8373 and we'll get you sorted immediately.</p>`,
          tags: ['login', 'access', 'sign in'],
          expanded: false
        },
        {
          title: 'How do I reset my password?',
          excerpt: 'Step-by-step guide to resetting your account password.',
          content: `<p>On the login page, click <strong>"Forgot password?"</strong> and enter your registered email. You'll receive a reset link within a few minutes.</p>`,
          fullContent: `<p>To reset your password:</p>
                        <ol>
                          <li>Go to <strong>academy.nexgencoaches.com/login</strong></li>
                          <li>Click "Forgot password?" below the sign-in form</li>
                          <li>Enter your registered email address</li>
                          <li>Click "Send reset link"</li>
                          <li>Check your inbox for the reset email (check spam if it doesn't arrive within 5 minutes)</li>
                          <li>Click the link in the email and create a new password</li>
                        </ol>
                        <p>Password reset links expire after 60 minutes. If yours has expired, simply repeat the process.</p>
                        <p>If you're still having trouble, contact us at +1 (248) 270-8373 or via the Contact page.</p>`,
          tags: ['password', 'reset', 'login'],
          expanded: false
        },
        {
          title: 'How do I navigate the platform dashboard?',
          excerpt: 'An overview of the main sections of the NEXGEN academy platform.',
          content: `<p>After logging in, you'll land on your Dashboard. From there you can access your courses, live sessions, the community, and the Barter Coaching Platform.</p>`,
          fullContent: `<p>Here's what you'll find in each section of the platform:</p>
                        <ul>
                          <li><strong>Dashboard</strong> — Your home screen. Shows your progress, upcoming live sessions, and recent activity.</li>
                          <li><strong>My Courses</strong> — All your course modules, videos, and exercises. Continue where you left off.</li>
                          <li><strong>Live Sessions</strong> — Schedule of upcoming live Zoom sessions. Access recordings of past sessions.</li>
                          <li><strong>Community</strong> — Connect with other NEXGEN students and alumni.</li>
                          <li><strong>Barter Coaching</strong> (Gold and Platinum only) — Find practice partners and log coaching hours.</li>
                          <li><strong>Resources</strong> — Downloads, templates, exam prep materials, and reference documents.</li>
                          <li><strong>Profile & Settings</strong> — Update your profile, timezone, notification preferences.</li>
                        </ul>`,
          tags: ['dashboard', 'navigation', 'platform'],
          expanded: false
        },
        // {
        //   title: 'How do I set my timezone for live sessions?',
        //   excerpt: 'Setting your timezone so live session times display correctly for your location.',
        //   content: `<p>Go to <strong>Profile → Settings → Timezone</strong> and select your local timezone. All session times on the platform will then display in your local time.</p>`,
        //   fullContent: `<p>The NEXGEN platform stores session times in UTC and displays them in your selected timezone. To set this up:</p>
        //                 <ol>
        //                   <li>Click your profile avatar in the top right corner</li>
        //                   <li>Select "Settings"</li>
        //                   <li>Under "Timezone Preferences," select your timezone from the dropdown</li>
        //                   <li>Save your settings</li>
        //                 </ol>
        //                 <p>Once set, all live session times, countdown timers, and calendar invitations will reflect your local time. This setting can be changed at any time.</p>`,
        //   tags: ['timezone', 'settings', 'live session'],
        //   expanded: false
        // }
      ]
    },
    {
      id: 'live-sessions',
      title: 'Live Sessions & Webinars',
      subtitle: 'How to join sessions, find recordings, and make the most of live instruction.',
      icon: 'fa-solid fa-video',
      tag: 'Sessions',
      tagColor: 'blue',
      articles: [
        {
          title: 'How do I join a live session?',
          excerpt: 'Steps to join a scheduled live session via Zoom.',
          content: `<p>Go to <strong>Live Sessions</strong> in the platform, find the upcoming session, and click <strong>"Join Session."</strong> This opens the Zoom link automatically. Sessions are live at the scheduled time only.</p>`,
          fullContent: `<p>Joining a NEXGEN live session:</p>
                        <ol>
                          <li>Log in to the platform at academy.nexgencoaches.com</li>
                          <li>Click "Live Sessions" in the main navigation</li>
                          <li>Find the session you want to attend</li>
                          <li>Click "Join Session" — this opens Zoom directly</li>
                          <li>If prompted, allow your browser to open Zoom or use the Zoom web client</li>
                        </ol>
                        <p><strong>Tip:</strong> Sessions typically open 5–10 minutes before the scheduled start time. Join early to test your audio/video.</p>
                        <p><strong>Missed it?</strong> All sessions are recorded. Recordings are available in the Live Sessions section within 24 hours of the session ending.</p>`,
          tags: ['live session', 'zoom', 'join'],
          expanded: false
        },
        // {
        //   title: 'Where can I find recordings of past sessions?',
        //   excerpt: 'How to access session recordings after a live session has taken place.',
        //   content: `<p>All recordings are in <strong>Live Sessions → Past Sessions</strong>. Recordings are uploaded within 24 hours of each session.</p>`,
        //   fullContent: `<p>To access past session recordings:</p>
        //                 <ol>
        //                   <li>Go to "Live Sessions" in the platform navigation</li>
        //                   <li>Select the "Past Sessions" or "Recordings" tab</li>
        //                   <li>Find the session by date or topic</li>
        //                   <li>Click "Watch Recording"</li>
        //                 </ol>
        //                 <p>Recordings are typically available within 24 hours of a session ending. They remain accessible throughout your enrollment and beyond.</p>`,
        //   tags: ['recording', 'replay', 'past session'],
        //   expanded: false
        // },
        {
          title: 'What if I can\'t attend a live session regularly?',
          excerpt: 'NEXGEN is designed for people who can\'t always attend live — here\'s how it works.',
          content: `<p>Every session is recorded. You never fall behind because of a scheduling conflict. The platform is designed specifically for working professionals who can't always attend live.</p>`,
          fullContent: `<p>NEXGEN is built for real life. Here's how we handle it:</p>
                        <ul>
                          <li><strong>Every live session is recorded</strong> and available within 24 hours</li>
                          <li>Recordings stay accessible for the duration of your enrollment</li>
                          <li>Course content is fully async — you can complete modules any time</li>
                          <li>The platform is available 24/7 from any device</li>
                        </ul>
                        <p>Live sessions are valuable — the interaction, Q&A, and real-time coaching practice make a real difference. But we know life happens. You can absolutely stay on track even if you miss sessions regularly.</p>`,
          tags: ['miss session', 'recording', 'schedule'],
          expanded: false
        }
      ]
    },
    {
      id: 'barter-coaching',
      title: 'Barter Coaching Platform',
      subtitle: 'How to find practice partners, schedule sessions, and log your ICF coaching hours.',
      icon: 'fa-solid fa-handshake',
      tag: 'Coaching Hours',
      tagColor: 'gold',
      articles: [
        {
          title: 'What is the Barter Coaching Platform?',
          excerpt: 'An overview of NEXGEN\'s peer coaching exchange system.',
          content: `<p>The Barter Coaching Platform connects Gold and Platinum students to exchange coaching sessions. You coach your peer; your peer coaches you. Both parties log required ICF hours.</p>`,
          fullContent: `<p>The Barter Coaching Platform is NEXGEN's exclusive solution to one of the biggest challenges new coaches face: finding people to coach to log their required ICF hours.</p>
                        <p>Here's how it works:</p>
                        <ol>
                          <li>Create your Barter Profile with your coaching interests and availability</li>
                          <li>Browse available partners or receive match suggestions</li>
                          <li>Schedule a session — you coach them for 45–60 minutes</li>
                          <li>Then they coach you for 45–60 minutes</li>
                          <li>Both parties log the session in the platform under their coaching hour records</li>
                        </ol>
                        <p>The platform tracks your hours automatically. You can see your progress toward the ICF requirement at any time.</p>`,
          tags: ['barter', 'coaching hours', 'practice'],
          expanded: false
        },
        {
          title: 'How do I log my coaching hours?',
          excerpt: 'Step-by-step instructions for recording completed coaching sessions.',
          content: `<p>After each session, go to <strong>Dashboard → Add Manual Time → Log Hours</strong> and record the date, duration, and client type. Your running total is always visible on your dashboard.</p>`,
          fullContent: `<p>To log coaching hours after a session:</p>
                        <ol>
                          <li>Go to "Barter Coaching" in the platform navigation</li>
                          <li>Click "My Sessions"</li>
                          <li>Click "Log a Session"</li>
                          <li>Enter the date, session duration, client type (peer/paid/volunteer), and any notes</li>
                          <li>Click "Save"</li>
                        </ol>
                        <p>Your dashboard will immediately update to show your current hour count. You can also export your coaching log as a PDF for your ICF application records.</p>
                        <p><strong>Important:</strong> The ICF requires documentation of coaching hours. Keep your log up to date — don't try to reconstruct it at the end.</p>`,
          tags: ['log hours', 'coaching hours', 'ICF', 'track'],
          expanded: false
        },
        {
          title: 'Can I also coach people outside the Barter Platform?',
          excerpt: 'How external coaching clients and paid hours count toward your ICF requirements.',
          content: `<p>Yes. You can log external coaching sessions too — with paid clients, volunteer clients, or anyone else you coach outside the platform. All hours count toward your ICF credential requirements.</p>`,
          fullContent: `<p>Absolutely. The Barter Platform is one way to get coaching hours — not the only way. You can also:</p>
                        <ul>
                          <li><strong>Coach paid clients</strong> you find on your own</li>
                          <li><strong>Coach colleagues or professionals</strong> in your network (pro-bono or paid)</li>
                          <li><strong>Use any other coaching exchange</strong> you participate in</li>
                        </ul>
                        <p>All sessions are logged in the same place — the "Log a Session" form in the platform. When you log the session, simply select the appropriate client type (barter, paid, volunteer).</p>
                        <p>For the ICF ACC credential: you need 100 total coaching hours, of which at least 75 must be paid. For PCC: 500 total, at least 450 paid.</p>`,
          tags: ['external coaching', 'paid clients', 'ICF hours'],
          expanded: false
        }
      ]
    },
    {
      id: 'icf-credential',
      title: 'ICF Credentialing',
      subtitle: 'Guidance on the ICF application process, exam, and submitting for your credential.',
      icon: 'fa-solid fa-award',
      tag: 'ICF',
      tagColor: 'green',
      articles: [
        {
          title: 'When can I apply for my ICF credential?',
          excerpt: 'Understanding the requirements you must complete before applying to the ICF.',
          content: `<p>You can apply to the ICF once you've completed your accredited training, accumulated the required coaching hours, completed 10 hours of mentor coaching, and passed the ICF Credentialing Exam.</p>`,
          fullContent: `<p>Before you submit your ICF credential application, you need:</p>
                        <h4>For ACC:</h4>
                        <ul>
                          <li>✓ 60+ hours from an ICF Level 1 (or equivalent) program (your NEXGEN Gold training covers this)</li>
                          <li>✓ 100 coaching hours (at least 75 paid)</li>
                          <li>✓ 10 hours of mentor coaching (built into your NEXGEN program)</li>
                          <li>✓ Pass the ICF Credentialing Exam</li>
                        </ul>
                        <h4>For PCC:</h4>
                        <ul>
                          <li>✓ 125+ hours from an ICF Level 2 program (your NEXGEN Platinum training covers this)</li>
                          <li>✓ 500 coaching hours (at least 450 paid)</li>
                          <li>✓ 10 hours of mentor coaching (included in your program)</li>
                          <li>✓ Pass the ICF Credentialing Exam</li>
                          <li>✓ Submit a coaching performance evaluation</li>
                        </ul>
                        <p>Your NEXGEN dashboard tracks your training hours automatically. The ICF application is submitted directly on the ICF website (icf.com).</p>`,
          tags: ['ICF', 'credential', 'apply', 'requirements'],
          expanded: false
        },
        {
          title: 'How do I prepare for the ICF Credentialing Exam?',
          excerpt: 'Resources and strategies for passing the situational judgment exam.',
          content: `<p>The ICF exam is a 2.5-hour situational judgment test — not memorization. NEXGEN's curriculum prepares you throughout your program. Exam prep materials are also in the Resources section of the platform.</p>`,
          fullContent: `<p>The ICF Credentialing Exam has 155 situational questions. It's a knowledge application exam, not a memory test. Here's how to prepare:</p>
                        <ul>
                          <li><strong>Know the 8 ICF Core Competencies deeply</strong> — not just what they are, but how they look in real coaching conversations</li>
                          <li><strong>Study the ICF Code of Ethics</strong> — several exam questions relate directly to ethical situations</li>
                          <li><strong>Practice situational thinking</strong> — for each scenario, ask: "What would a good ICF coach do here?"</li>
                          <li><strong>Use the NEXGEN exam prep resources</strong> in your platform Resources section</li>
                          <li><strong>Lyssa deHart's exam preparation content</strong> is included in your Gold and Platinum programs</li>
                        </ul>
                        <p>Most NEXGEN students report that their program training prepares them well for the exam. Don't cram — consistent engagement throughout your program is the most effective prep.</p>`,
          tags: ['ICF exam', 'credentialing exam', 'prepare', 'pass'],
          expanded: false
        },
        {
          title: 'What documentation do I need for my ICF application?',
          excerpt: 'What to gather and prepare before submitting your ICF credential application.',
          content: `<p>You'll need: proof of your training (a certificate from NEXGEN/Lyssa deHart Coaching), your coaching hour log, confirmation of mentor coaching hours, and exam results. Your NEXGEN platform generates all of this.</p>`,
          fullContent: `<p>For your ICF credential application, you'll need the following documents:</p>
                        <ul>
                          <li><strong>Training verification</strong> — A certificate from Lyssa deHart Coaching (your accredited training provider), confirming your hours</li>
                          <li><strong>Coaching hour log</strong> — Exportable from your NEXGEN platform as a PDF. Shows dates, hours, client types.</li>
                          <li><strong>Mentor coaching confirmation</strong> — Your mentor coach will provide a signed document confirming your 10 hours</li>
                          <li><strong>ICF exam results</strong> — Automatically reported to the ICF when you pass</li>
                          <li><strong>For PCC: coaching recording evaluation</strong> — Submitted via the ICF's performance evaluation process</li>
                        </ul>
                        <p>Your NEXGEN platform is designed to produce all required documentation. Contact us if you need any document regenerated or have questions about the format.</p>`,
          tags: ['ICF application', 'documentation', 'credential'],
          expanded: false
        }
      ]
    },
    {
      id: 'account-billing',
      title: 'Account & Billing',
      subtitle: 'Managing your account, upgrading your program, and payment questions.',
      icon: 'fa-solid fa-credit-card',
      tag: 'Account',
      tagColor: 'slate',
      articles: [
        {
          title: 'How do I upgrade from Silver to Gold or Gold to Platinum?',
          excerpt: 'The process for upgrading your NEXGEN program at any time.',
          content: `<p>Contact us by phone or via the Contact page to arrange an upgrade. You pay only the price difference between your current program and the next tier. Your progress and hours transfer completely.</p>`,
          fullContent: `<p>Upgrading your NEXGEN program is straightforward:</p>
                        <ol>
                          <li>Contact us at +1 (248) 270-8373 or via the Contact page</li>
                          <li>Tell us you'd like to upgrade and which tier you're moving to</li>
                          <li>You'll pay only the difference in program price (e.g., Silver to Gold = $2,000 upgrade fee)</li>
                          <li>Your account is upgraded immediately upon payment confirmation</li>
                          <li>All your prior progress, completed modules, and logged hours carry over</li>
                        </ol>
                        <p>There is no deadline or penalty for upgrading. You can do it one month into Silver or two years later — the upgrade credit never expires.</p>`,
          tags: ['upgrade', 'silver to gold', 'gold to platinum'],
          expanded: false
        },
        {
          title: 'How do I update my billing or payment information?',
          excerpt: 'How to update your payment details or set up a payment plan.',
          content: `<p>For billing changes or payment plan arrangements, contact us directly at +1 (248) 270-8373 or via the Contact page. We don't store card details in the platform — all billing is handled securely.</p>`,
          fullContent: `<p>NEXGEN does not store payment card details directly in the platform. For any billing-related changes:</p>
                        <ul>
                          <li><strong>Update a payment method</strong> — Contact us and we'll send a secure payment link</li>
                          <li><strong>Set up or modify a payment plan</strong> — Call +1 (248) 270-8373 or contact us via the form</li>
                          <li><strong>Request a receipt or invoice</strong> — Contact us with your enrollment date and email</li>
                          <li><strong>Questions about a charge</strong> — Contact us and we'll resolve it promptly</li>
                        </ul>
                        <p>Our team responds within one business day for all billing inquiries.</p>`,
          tags: ['billing', 'payment', 'invoice'],
          expanded: false
        },
        {
          title: 'How do I update my profile or contact information?',
          excerpt: 'Where to update your name, email, phone number, and profile photo.',
          content: `<p>Go to <strong>Profile → Edit Profile</strong> to update your name, photo, bio, and contact details. To change your login email, contact us as this requires account verification.</p>`,
          fullContent: `<p>To update your profile information:</p>
                        <ol>
                          <li>Click your avatar in the top-right corner of the platform</li>
                          <li>Select "Edit Profile"</li>
                          <li>Update your name, bio, photo, phone number, and social links</li>
                          <li>Click "Save Changes"</li>
                        </ol>
                        <p><strong>Changing your login email:</strong> For security, email address changes require verification. Contact us and we'll walk you through the process.</p>
                        <p><strong>Changing your password:</strong> Go to Profile → Security → Change Password.</p>`,
          tags: ['profile', 'update', 'email', 'account settings'],
          expanded: false
        }
      ]
    }
  ];

  ngOnInit(): void {
    this.seoService.applySeo({
  title: 'NEXGEN Help Center | Login, Live Sessions, Coaching Hours and ICF Support',
  description: 'Get help with logging in, live sessions, barter coaching, ICF credential questions, billing, and platform support at NEXGEN Executive Coaching Academy.',
  keywords: 'NEXGEN help center, NEXGEN support, coaching academy help, live session help, barter coaching help, ICF credential support, login help, billing support',
  canonicalUrl: 'https://academy.nexgencoaches.com/help-center',
  ogTitle: 'NEXGEN Help Center | Login, Live Sessions, Coaching Hours and ICF Support',
  ogDescription: 'Get help with logging in, live sessions, barter coaching, ICF credential questions, billing, and platform support at NEXGEN Executive Coaching Academy.',
  ogType: 'website',
  twitterTitle: 'NEXGEN Help Center | Login, Live Sessions, Coaching Hours and ICF Support',
  twitterDescription: 'Find support for platform access, webinars, coaching hours, ICF questions, and billing.',
  robots: 'index, follow',
  schema: [
    {
      '@type': 'WebPage',
      '@id': 'https://academy.nexgencoaches.com/help-center',
      'url': 'https://academy.nexgencoaches.com/help-center',
      'name': 'NEXGEN Help Center | Login, Live Sessions, Coaching Hours and ICF Support',
      'description': 'Get help with logging in, live sessions, barter coaching, ICF credential questions, billing, and platform support at NEXGEN Executive Coaching Academy.'
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'How do I log in to the NEXGEN platform?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Go to academy.nexgencoaches.com/login and enter your email and password. If you do not receive your login credentials after enrolling, check spam and then contact support.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How do I reset my password?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'On the login page, click Forgot password, enter your registered email, and use the reset link sent to your inbox.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How do I join a live session?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Go to Live Sessions inside the platform, find the scheduled session, and click Join Session to open the Zoom link.'
          }
        },
        {
          '@type': 'Question',
          'name': 'What is the Barter Coaching Platform?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'The Barter Coaching Platform connects Gold and Platinum students so they can exchange coaching sessions, practice with peers, and log coaching hours.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How do I log my coaching hours?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'After each session, go to the platform area for coaching or manual time logging, enter the date, duration, and client type, and save the session.'
          }
        },
        {
          '@type': 'Question',
          'name': 'When can I apply for my ICF credential?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'You can apply once you complete your accredited training, required coaching hours, mentor coaching, and the ICF exam or related evaluation requirements.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How do I upgrade from Silver to Gold or Gold to Platinum?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'Contact NEXGEN support to upgrade. You pay only the price difference, and your progress and hours carry over.'
          }
        },
        {
          '@type': 'Question',
          'name': 'How do I contact NEXGEN support?',
          'acceptedAnswer': {
            '@type': 'Answer',
            'text': 'You can contact support by phone, through the contact page, or by using the Help Center contact options. The team responds within one business day.'
          }
        }
      ]
    }
  ]
});

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  setSearch(term: string): void {
    this.searchTerm = term;
    this.onSearch();
  }

  onSearch(): void {
    const q = this.searchTerm.toLowerCase().trim();
    if (!q) {
      this.searchResults = [];
      return;
    }

    const results: SearchResult[] = [];
    this.helpCategories.forEach(cat => {
      cat.articles.forEach(article => {
        const searchable = (article.title + ' ' + article.excerpt + ' ' + article.fullContent + ' ' + article.tags.join(' ')).toLowerCase();
        if (searchable.includes(q)) {
          results.push({
            title: article.title,
            excerpt: article.excerpt,
            fullContent: article.fullContent,
            categoryLabel: cat.title,
            categoryIcon: cat.icon,
            tags: article.tags
          });
        }
      });
    });

    this.searchResults = results;
  }


  ngOnDestroy(): void {
  this.seoService.clearSeo();
  document.body.style.overflow = '';
}

  clearSearch(): void {
    this.searchTerm = '';
    this.searchResults = [];
  }

  openArticle(result: SearchResult): void {
    this.openedArticle = result;
    this.helpfulSubmitted = false;
    document.body.style.overflow = 'hidden';
  }

  closeArticle(): void {
    this.openedArticle = null;
    document.body.style.overflow = '';
  }

  toggleArticle(article: Article): void {
    article.expanded = !article.expanded;
  }

  markHelpful(helpful: boolean): void {
    this.helpfulSubmitted = true;
    this.helpfulResponse = helpful
      ? 'Glad that helped! Feel free to explore more articles.'
      : 'Thanks for the feedback. Contact us if you need more help.';
  }
}